// PartLabelTemplate.tsx
import React from "react";
import type { PartLabelDisplay } from "../../models/LabelDisplay";

export type PartLabelCoreTemplateProps = {
  display: PartLabelDisplay;

  // values
  date?: string;
  tagNo?: string;
  logoSrc?: string;
  secondLogoSrc?: string;

  make?: string;
  model?: string;
  category?: string;

  qrSrc?: string;
  price?: string; // kept for backwards compatibility, but not rendered

  vrm?: string;
  year?: string;
  engineCode?: string;
  vin?: string;
  fuelType?: string;

  position?: string;
  qc?: string;
  imaged?: string;
};

const textOrNull = (enabled: boolean, value?: string) => {
  const v = (value ?? "").trim();
  return enabled && v ? v : null;
};

const getVrmFontPx = (vrm: string, spx: (px: number) => string) => {
  // Scale aggressively for short VRMs, gently for longer ones.
  const len = (vrm ?? "").trim().length;

  // Typical UK plates are 7 chars, sometimes 6/8.
  // Tune to fill the middle column without wrapping.
  if (len <= 5) return spx(30);
  if (len === 6) return spx(28);
  if (len === 7) return spx(26);
  if (len === 8) return spx(24);
  return spx(22);
};

export default function PartLabelCoreTemplate(props: PartLabelCoreTemplateProps) {
  const { display } = props;

  // --- Base label design size
  const BASE_W = 10; // cm
  const BASE_H = 5;  // cm

  // --- Target label size
  const W = display.widthCm ?? BASE_W;
  const H = display.heightCm ?? BASE_H;

  // --- Scale factors
  const sx = W / BASE_W;
  const sy = H / BASE_H;

  // Uniform scale for font/pixel spacing
  const s = Math.min(sx, sy);

  // Helpers
  const cw = (cm: number) => `${(cm * sx).toFixed(4)}cm`;
  const ch = (cm: number) => `${(cm * sy).toFixed(4)}cm`;
  const spx = (px: number) => `${Math.round(px * s)}px`;

  // ======== Vehicle Stack ========
  const vrm = textOrNull(display.vehicleStack.vrm, props.vrm);

  const stackLines = [
    textOrNull(display.vehicleStack.year, props.year),
    textOrNull(display.vehicleStack.engineCode, props.engineCode),
    textOrNull(display.vehicleStack.vin, props.vin),
    textOrNull(display.vehicleStack.fuelType, props.fuelType),
  ].filter(Boolean) as string[];

  return (
    <html>
      <head>
        <meta
          name="viewport"
          content="width=device-width,initial-scale=1,maximum-scale=1,minimum-scale=1,user-scalable=no"
        />
      </head>

      <body style={{ margin: 0, padding: 0 }}>
        <div
          style={{
            width: `${W}cm`,
            height: `${H}cm`,
            display: "grid",
            gridTemplateColumns: `${cw(9)} ${cw(1)}`,
            gridTemplateRows: `${ch(1)} ${ch(1)} ${ch(3)}`,
            fontFamily: "Arial,Helvetica,sans-serif",
            boxSizing: "border-box",
            overflow: "hidden",
          }}
        >
          {/* Right vertical date strip (optional) */}
          {display.date && (
            <div
              style={{
                gridColumn: 2 as any,
                gridRow: "1/span 3",
                writingMode: "vertical-rl",
                textOrientation: "mixed",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontWeight: 700,
                transform: "rotate(180deg)",
              }}
            >
              {props.date ?? ""}
            </div>
          )}

          {/* Header row */}
          <div
            style={{
              gridColumn: 1 as any,
              gridRow: 1 as any,
              display: "grid",
              gridTemplateColumns: `${cw(4)} ${cw(5)}`,
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "start",
                fontWeight: 700,
                fontSize: spx(16),
                height: ch(1),
              }}
            >
              {display.tagNo ? (props.tagNo ?? "") : null}
            </div>

            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-end",
                paddingLeft: spx(10),
                height: ch(1),
              }}
            >
              {display.logo && (props.logoSrc || props.secondLogoSrc) ? (
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "flex-end",
                    gap: spx(12),
                    height: "100%",
                    maxWidth: "100%",
                    overflow: "hidden",
                  }}
                >
                  {props.logoSrc ? (
                    <img
                      alt="Logo"
                      src={props.logoSrc}
                      style={{ height: "100%", width: "auto", objectFit: "contain" }}
                    />
                  ) : null}

                  {props.secondLogoSrc ? (
                    <img
                      alt="RS Core"
                      src={props.secondLogoSrc}
                      style={{ height: "85%", width: "auto", objectFit: "contain" }}
                    />
                  ) : null}
                </div>
              ) : null}
            </div>
          </div>

          {/* Make/Model/Category line */}
          <div
            style={{
              gridColumn: 1 as any,
              gridRow: 2 as any,
              display: "flex",
              alignItems: "center",
              justifyContent: "start",
              fontWeight: 700,
              height: ch(1),
              lineHeight: 1,
              fontSize: spx(16),
            }}
          >
            {display.makeModelCategoryLine
              ? [props.make, props.model].filter(Boolean).join(" ") +
                (props.category ? ` • ${props.category}` : "")
              : null}
          </div>

          {/* Main row */}
          <div
            style={{
              gridColumn: 1 as any,
              gridRow: 3 as any,
              display: "grid",
              // Give the VRM column more room: 2 / 4.8 / 2.2 (was 2 / 4 / 3)
              gridTemplateColumns: `${cw(2)} ${cw(4.8)} ${cw(2.2)}`,
            }}
          >
            {/* Left: QR (full height) */}
            <div style={{ display: "flex", flexDirection: "column" }}>
              <div
                style={{
                  flex: "1 1 auto",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  height: ch(3),
                  width: cw(2),
                }}
              >
                {display.qr && props.qrSrc ? (
                  <img
                    alt="QR"
                    src={props.qrSrc}
                    style={{ width: "100%", height: "100%", objectFit: "contain" }}
                  />
                ) : null}
              </div>
            </div>

            {/* Middle: VRM very large + details */}
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between",
                textAlign: "center",
                height: ch(3),
                paddingTop: spx(1),
                paddingBottom: spx(1),
              }}
            >
              {vrm ? (
                <div
                  style={{
                    fontSize: getVrmFontPx(vrm, spx),
                    fontWeight: 900,
                    letterSpacing: spx(1),
                    lineHeight: 1,
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    // make it feel like it "owns" the space
                    marginTop: spx(2),
                  }}
                >
                  {vrm}
                </div>
              ) : (
                <div />
              )}

              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "end",
                  gap: spx(2),
                  lineHeight: 1.1,
                }}
              >
                {stackLines.map((line, idx) => (
                  <div key={idx} style={{ fontSize: spx(12) }}>
                    {line}
                  </div>
                ))}
              </div>
            </div>

            {/* Right: position/QC/img */}
            <div
              style={{
                display: "grid",
                gridTemplateRows: `${ch(1.5)} ${ch(1.5)}`,
                gridTemplateColumns: `${cw(1.1)} ${cw(1.1)}`,
                height: "100%",
              }}
            >
              <div
                style={{
                  gridColumn: "1/span 2",
                  display: "flex",
                  alignItems: "end",
                  justifyContent: "center",
                  fontWeight: 700,
                  textAlign: "center",
                }}
              >
                {display.position ? (props.position ?? "-") : null}
              </div>

              <div style={{ display: "flex", alignItems: "end", justifyContent: "center", textAlign: "center" }}>
                {display.qc ? (props.qc ?? "-") : null}
              </div>

              <div style={{ display: "flex", alignItems: "end", justifyContent: "center", textAlign: "center" }}>
                {display.imaged ? (props.imaged ?? "-") : null}
              </div>
            </div>
          </div>
        </div>
      </body>
    </html>
  );
}
